/* eslint-disable */
import React, { useEffect, useMemo, useState } from 'react';

// MUI
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';

// Mantine/Mantis card wrapper
import MainCard from 'components/MainCard';

// Icons (ant design)
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';

import api from 'api/axios';

// -------- helper: confirm dialog ----------
function ConfirmDialog({ open, title, body, confirmText = 'Confirm', onClose, onConfirm, loading, destructive }) {
  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" sx={{ opacity: 0.9, whiteSpace: 'pre-wrap' }}>
          {body}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={onConfirm} disabled={loading} variant="contained" color={destructive ? 'error' : 'primary'}>
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Profile() {
  const AUTH = useMemo(
    () => ({
      withCredentials: true,
      headers: { 'X-API-Key': 'secret123' }
    }),
    []
  );

  // table & pagination
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1); // 1-based
  const [perPage, setPerPage] = useState(100);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // dialogs state
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  // form fields (ADD)
  const [addUsername, setAddUsername] = useState('');
  const [addPass, setAddPass] = useState('');
  const [addRole, setAddRole] = useState('user'); // admin | user

  // form fields (EDIT)
  const [editId, setEditId] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [editPass, setEditPass] = useState('');
  const [editRole, setEditRole] = useState('user'); // admin | user

  // confirm dialog
  const [confirm, setConfirm] = useState({
    open: false,
    title: '',
    body: '',
    confirmText: 'Confirm',
    destructive: false,
    onConfirm: null
  });
  const [confirmLoading, setConfirmLoading] = useState(false);

  const openConfirm = ({ title, body, confirmText, destructive, onConfirm }) => {
    setConfirm({
      open: true,
      title,
      body,
      confirmText: confirmText || 'Confirm',
      destructive: !!destructive,
      onConfirm
    });
  };

  const closeConfirm = () => {
    if (confirmLoading) return;
    setConfirm((c) => ({ ...c, open: false }));
  };

  // -------- API ----------
  const fetchRows = async () => {
    try {
      setLoading(true);

      // GET /api/users/all/:limit/:page
      const res = await api.get(`/api/users/all/${perPage}/${page}`, AUTH);

      // response: { limit, page, total, users: [...] }
      const payload = res?.data || {};
      const list = Array.isArray(payload.users) ? payload.users : [];

      setRows(
        list.map((d) => ({
          user_id: d.id,
          user_username: d.username,
          user_role: d.role,
          user_created_at: d.created_at,
          user_created_by: d.created_by,
          user_updated_at: d.updated_at,
          user_updated_by: d.updated_by
        }))
      );

      const limit = Number(payload.limit) || perPage;
      const total = Number(payload.total) || 0;

      setTotalCount(total);
      setTotalPages(Math.max(1, Math.ceil(total / limit)));
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || err?.message || 'Gagal memuat users');
      setRows([]);
      setTotalCount(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage]);

  const onAdd = async () => {
    const uname = addUsername.trim();
    if (!uname) return alert('Username diperlukan');
    if ((addPass || '').length < 6) return alert('Password minimal 6 karakter');

    // POST /api/users { username, password, role }
    await api.post(`/api/users`, { username: uname, password: addPass, role: addRole }, AUTH);

    setAddOpen(false);
    setAddUsername('');
    setAddPass('');
    setAddRole('user');
    fetchRows();
  };

  const openEdit = async (u) => {
    try {
      // GET /api/users/:id
      const res = await api.get(`/api/users/${u.user_id}`, AUTH);
      const d = res.data || {};

      setEditId(d.id);
      setEditUsername(d.username ?? u.user_username);
      setEditRole(d.role ?? u.user_role ?? 'user');
      setEditPass('');
      setEditOpen(true);
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || err?.message || 'Gagal buka data user');
    }
  };

  const onSaveEdit = async () => {
    if (!editId) return;

    // PUT /api/users/:id { username, password, role }
    // password optional: kirim hanya kalau diisi
    const payload = {
      username: editUsername.trim(),
      role: editRole
    };
    if ((editPass || '').trim()) payload.password = editPass;

    await api.put(`/api/users/${editId}`, payload, AUTH);

    setEditOpen(false);
    fetchRows();
  };

  const onDelete = async (u) => {
    // DELETE /api/users/:id
    await api.delete(`/api/users/${u.user_id}`, AUTH);
    setRows((prev) => prev.filter((x) => x.user_id !== u.user_id));
    fetchRows();
  };

  return (
    <Box sx={{ p: 2 }}>
      <MainCard
        title={
          <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
            <Typography variant="h5">Users</Typography>
            <Chip variant="outlined" size="small" label={`Total on this page: ${rows.length}`} />
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" onClick={() => setAddOpen(true)}>
              Add User
            </Button>
          </Stack>
        }
        content={false}
      >
        <Box sx={{ p: 2 }}>
          <Box sx={{ overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: 70, fontWeight: 700 }}>ID</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Username</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Actions</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ opacity: 0.8 }}>
                      {loading ? 'Loading...' : 'Belum ada user.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((u) => (
                    <TableRow key={u.user_id}>
                      <TableCell>{u.user_id}</TableCell>
                      <TableCell>{u.user_username}</TableCell>
                      <TableCell>
                        <Chip size="small" label={u.user_role} variant="filled" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} flexWrap="wrap">
                          <Button size="small" variant="outlined" onClick={() => openEdit(u)} startIcon={<EditOutlined />}>
                            Edit
                          </Button>

                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() =>
                              openConfirm({
                                title: 'Hapus User?',
                                body: `Anda yakin ingin menghapus user "${u.user_username}"?\nTindakan ini tidak bisa dibatalkan.`,
                                confirmText: 'Delete',
                                destructive: true,
                                onConfirm: async () => onDelete(u)
                              })
                            }
                            startIcon={<DeleteOutlined />}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Pagination */}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Total: {totalCount} | Page {page} / {totalPages}
            </Typography>

            <Box sx={{ flex: 1 }} />

            <Stack direction="row" spacing={1} alignItems="center">
              <Button size="small" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1 || totalCount === 0}>
                Prev
              </Button>
              <Button
                size="small"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || totalCount === 0}
              >
                Next
              </Button>

              <Select
                size="small"
                value={perPage}
                onChange={(e) => {
                  setPage(1);
                  setPerPage(Number(e.target.value));
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <MenuItem key={n} value={n}>
                    {n} / page
                  </MenuItem>
                ))}
              </Select>
            </Stack>
          </Stack>
        </Box>
      </MainCard>

      {/* ADD DIALOG */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField label="Username" value={addUsername} onChange={(e) => setAddUsername(e.target.value)} fullWidth />
            <TextField
              label="Password (min 6 char)"
              type="password"
              value={addPass}
              onChange={(e) => setAddPass(e.target.value)}
              fullWidth
            />

            <FormControl fullWidth>
              <Select value={addRole} onChange={(e) => setAddRole(e.target.value)}>
                <MenuItem value="admin">admin</MenuItem>
                <MenuItem value="user">user</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() =>
              openConfirm({
                title: 'Create user?',
                body: `Username: ${addUsername}\nRole: ${addRole}`,
                confirmText: 'Create',
                onConfirm: onAdd
              })
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* EDIT DIALOG */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <TextField label="Username" value={editUsername} onChange={(e) => setEditUsername(e.target.value)} fullWidth />
            <TextField
              label="New Password (optional)"
              type="password"
              value={editPass}
              onChange={(e) => setEditPass(e.target.value)}
              fullWidth
              placeholder="leave blank to keep"
            />

            <FormControl fullWidth>
              <Select value={editRole} onChange={(e) => setEditRole(e.target.value)}>
                <MenuItem value="admin">admin</MenuItem>
                <MenuItem value="user">user</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() =>
              openConfirm({
                title: 'Save changes?',
                body: `Username: ${editUsername}\nRole: ${editRole}\n${editPass ? 'Password: (will be updated)' : 'Password: (unchanged)'}`,
                confirmText: 'Save',
                onConfirm: onSaveEdit
              })
            }
          >
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* CONFIRM */}
      <ConfirmDialog
        open={confirm.open}
        title={confirm.title}
        body={confirm.body}
        confirmText={confirm.confirmText}
        destructive={confirm.destructive}
        loading={confirmLoading}
        onClose={closeConfirm}
        onConfirm={async () => {
          if (!confirm.onConfirm) return;
          try {
            setConfirmLoading(true);
            await confirm.onConfirm();
            setConfirm((c) => ({ ...c, open: false }));
          } finally {
            setConfirmLoading(false);
          }
        }}
      />
    </Box>
  );
}
